import React, { useMemo, useState, MutableRefObject } from 'react'
import { Modal } from '../components/Modal'

type ModalProps<T> = {
    children?: JSX.Element
    confirmLabel?: string
    cancelLabel?: string
    disableConfirm?: boolean
    disableCancel?: boolean
    widthPx?: number
    hideActions?: boolean
    renderChild?: (props: { data?: T }) => JSX.Element
}
type ModalComponent<T> = (props: ModalProps<T>) => JSX.Element
type ToggleFn<T> = (params: T | boolean) => Promise<void>
type ContentState<T = boolean> = {
    res: () => void
    rej: () => void
    data?: T
}
type Return<T = boolean> = [ModalComponent<T>, ToggleFn<T>]

export const useModal = <T,>(params?: {
    onConfirm?: () => void
    onCancel?: () => void
    containerRef?: MutableRefObject<HTMLDivElement | null>
}): Return<T> => {
    const { onConfirm, onCancel, containerRef } = params || {}
    const [modalParams, setVisibilityCallbacks] = useState<ContentState<T> | null>(null)

    const Component = useMemo(
        () =>
            ({
                children,
                confirmLabel,
                cancelLabel,
                disableConfirm,
                disableCancel,
                hideActions,
                widthPx: width,
                renderChild,
            }: ModalProps<T>) => {
                let content = children
                if (renderChild && modalParams?.data)
                    content = renderChild({ data: modalParams.data })
                if (!content) {
                    // eslint-disable-next-line no-console
                    console.error('Invalid modal content ', JSON.stringify(content))
                    return <div />
                }
                return (
                    <Modal
                        confirmLabel={confirmLabel}
                        cancelLabel={cancelLabel}
                        widthPx={width}
                        isVisible={Boolean(modalParams)}
                        disableCancel={disableCancel}
                        disableConfirm={disableConfirm}
                        hideActions={hideActions}
                        containerRef={containerRef}
                        onClose={() => {
                            modalParams?.rej()
                            onCancel?.()
                            setVisibilityCallbacks(null)
                        }}
                        onCancel={
                            onCancel
                                ? () => {
                                      modalParams?.rej()
                                      onCancel?.()
                                      setVisibilityCallbacks(null)
                                  }
                                : undefined
                        }
                        onConfirm={
                            onConfirm
                                ? () => {
                                      onConfirm?.()
                                      modalParams?.res()
                                      setVisibilityCallbacks(null)
                                  }
                                : undefined
                        }
                        content={content}
                    />
                )
            },
        [containerRef, modalParams, onConfirm, onCancel]
    )

    return [
        Component,
        (nextVisibility) => {
            if (nextVisibility) {
                let data: T
                if (typeof nextVisibility !== 'boolean') data = nextVisibility
                return new Promise((res, rej) => {
                    setVisibilityCallbacks({ res, rej, data })
                })
            }
            if (!nextVisibility && !!modalParams) {
                const { rej } = modalParams
                setVisibilityCallbacks(null)
                rej()
                return Promise.resolve()
            }
            return Promise.resolve()
        },
    ]
}
